import { Injectable } from '@angular/core';
import { LuceneExpression } from 'lucene';

import { DatacenterFiltersConfiguration } from '../datacenter/datacenter-filters-configuration';
import {
	DateRange,
	DateRangeFilter,
	FilterType as FilterTypeValue,
	NumberFilter,
	NumberRange,
	OrderNumber,
	OrderNumberFilter,
	TextFilter
} from '../models/ag-grid';
import { UserDatacenterView } from '../models/datacenter-aggrid-view';
import {
	DatacenterFreeTextFilter,
	DocumentOrderNumberFilter,
	FilterType,
	OrderNumberFilter as DatacenterOrderNumberFilter,
	ShipmentOrderNumberFilter
} from '../models/datacenter-filters';
import { isNumber } from '../utils/number.format';

interface FilterValues {
	item?: string;
	from?: string | Date;
	reference?: string;
	to?: string | Date;
}

type AFilterFilterType = 'date' | 'number' | 'text';

type AgFilterOperator = 'OR' | 'AND';

type AgFilterType = 'inRange'
	| 'greaterThan' | 'greaterThanOrEqual'
	| 'lessThan' | 'lessThanOrEqual'
	| 'contains' | 'endsWith';

interface AgFilterBase<FilterType extends AFilterFilterType> {
	filterType: FilterType;
}

interface AgDateFilter extends AgFilterBase<'date'> {
	type: AgFilterType;
	dateFrom: string;
	dateTo?: string;
}

interface AgNumberFilter extends AgFilterBase<'number'> {
	type: AgFilterType;
	filter: number;
	filterTo?: number;
}

interface AgTextFilter extends AgFilterBase<'text'> {
	type: AgFilterType;
	filter: string;
	filterTo?: string;
}

interface AgOperatorFilter<FilterType extends AFilterFilterType> extends AgFilterBase<FilterType> {
	operator: AgFilterOperator;
	condition1: AgFilterBase<FilterType>;
	condition2: AgFilterBase<FilterType>;
}

@Injectable(
	{ providedIn: 'root' }
)
export class DatacenterFilterUtilsService {
	constructor(private readonly defaultFilterConfiguration: DatacenterFiltersConfiguration) {}

	getPreparedFilters(view: UserDatacenterView) {
		const resultFilter: FilterType[] = [];
		if (!view.filterState) {
			return resultFilter;
		}
		const { filterState } = view;
		for (const item of filterState) {
			const filterType = this.defaultFilterConfiguration.getFilter(item.id, view.type);
			if (!filterType) {
				continue;
			}
			if (filterType instanceof DatacenterFreeTextFilter
				|| filterType instanceof DatacenterOrderNumberFilter
				|| filterType instanceof DocumentOrderNumberFilter
				|| filterType instanceof ShipmentOrderNumberFilter) {
				filterType.enableExactTextMatching();
			}
			// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument
			filterType.addValues(<any> item.values);
			resultFilter.push(filterType);
		}

		return resultFilter;
	}

	/**
	 * Get filters for AgGrid native model
	 * @param view - datacenter view
	 * @returns filters using AgGrid native model
	 */
	getAgFilters(view: UserDatacenterView): Record<string, unknown> | undefined {
		if (!view.filterState || view.filterState.length === 0) {
			return undefined;
		}
		const agFilters: Record<string, AgFilterBase<AFilterFilterType> | undefined> = {};
		for (const filter of view.filterState) {
			for (const values of <(FilterValues | string)[]> filter.values) {
				const agFilter = this.getAgDateFilter(values)
					?? this.getAgNumberFilter(values)
					?? this.getAgOrderNumberFilter(values)
					?? this.getAgTextFilter(values);
				if (!agFilter) {
					continue;
				}
				const currentFilter = agFilters[filter.id];
				if (!currentFilter) {
					agFilters[filter.id] = agFilter;
					continue;
				}
				const operator: AgOperatorFilter<typeof agFilter.filterType> = {
					filterType: agFilter.filterType,
					operator: 'OR',
					condition1: agFilter,
					condition2: currentFilter
				};
				agFilters[filter.id] = operator;
			}
		}
		if (Object.keys(agFilters).length === 0) {
			return undefined;
		}
		return agFilters;
	}

	/**
	 * Get filters for SteelUser custom filter components
	 * @param view - datacenter view
	 * @returns filters using model of custom steeluser filter components
	 */
	getSteelUserFilters(view: UserDatacenterView): Record<string, unknown> | undefined {
		if (!view.filterState || view.filterState.length === 0) {
			return undefined;
		}
		const filters: Record<string, unknown> = {};
		for (const filter of view.filterState) {
			for (const values of <(FilterValues | string)[]> filter.values) {
				const currentFilter = filters[filter.id];
				const steeluserFilter = this.getSteelUserTextFilter(currentFilter, values)
					?? this.getSteelUserDateRangeFilter(currentFilter, values)
					?? this.getSteelUserNumberFilter(currentFilter, values)
					?? this.getSteelUserOrderNumberFilter(currentFilter, values);
				if (!steeluserFilter) {
					continue;
				}
				filters[filter.id] = steeluserFilter;
			}
		}
		if (Object.keys(filters).length === 0) {
			return undefined;
		}
		return filters;
	}

	getLuceneFromAllFilters(view: UserDatacenterView) {
		const allFilters = this.getPreparedFilters(view);
		let result: LuceneExpression | undefined;
		for (const filter of allFilters) {
			const newNode = filter.getLuceneExpression();
			if (newNode && 'parenthesized' in newNode) {
				newNode.parenthesized = true;
			}
			result = this.createLuceneNode(result, newNode);
		}

		return result;
	}

	private getAgDateFilter(values: FilterValues | string): AgDateFilter | undefined {
		if (typeof values === 'string') {
			return undefined;
		}
		const filterType = 'date';
		let fromFilter: AgDateFilter | undefined;
		let toFilter: AgDateFilter | undefined;
		const from = this.getDate(values.from);
		if (from) {
			fromFilter = {
				filterType,
				type: 'greaterThan',
				dateFrom: from.toISOString()
			};
		}
		const to = this.getDate(values.to);
		if (to) {
			toFilter = {
				filterType,
				type: 'lessThan',
				dateFrom: to.toISOString()
			};
		}
		if (fromFilter && toFilter) {
			return {
				filterType,
				type: 'inRange',
				dateFrom: fromFilter.dateFrom,
				dateTo: toFilter.dateFrom,
			};
		} else if (fromFilter) {
			return fromFilter;
		} else if (toFilter) {
			return toFilter;
		}
		return undefined;
	}

	private getAgNumberFilter(values: FilterValues | string): AgNumberFilter | undefined {
		if (typeof values === 'string') {
			return undefined;
		}
		const filterType = 'number';
		let fromFilter: AgNumberFilter | undefined;
		let toFilter: AgNumberFilter | undefined;
		if (values.from && !(values.from instanceof Date)) {
			const filter = Number.parseFloat(values.from);
			if (!Number.isNaN(filter)) {
				fromFilter = {
					filterType,
					type: 'greaterThanOrEqual',
					filter
				};
			}
		}
		if (values.to && !(values.to instanceof Date)) {
			const filter = Number.parseFloat(values.to);
			if (!Number.isNaN(filter)) {
				toFilter = {
					filterType,
					type: 'lessThanOrEqual',
					filter
				};
			}
		}
		if (fromFilter && toFilter) {
			return {
				filterType,
				type: 'inRange',
				filter: fromFilter.filter,
				filterTo: toFilter.filter,
			};
		} else if (fromFilter) {
			return fromFilter;
		} else if (toFilter) {
			return toFilter;
		}
		return undefined;
	}

	private getAgOrderNumberFilter(values: FilterValues | string): AgTextFilter | AgOperatorFilter<'text'> | undefined {
		if (typeof values === 'string') {
			return undefined;
		}
		const filterType = 'text';
		let referenceFilter: AgTextFilter | undefined;
		let itemFilter: AgTextFilter | undefined;
		if (values.reference?.trim()) {
			referenceFilter = {
				filterType,
				type: 'contains',
				filter: values.reference.trim()
			};
		}
		if (values.item?.trim()) {
			itemFilter = {
				filterType,
				type: 'endsWith',
				filter: `/${ values.item.trim() }`
			};
		}
		if (referenceFilter && itemFilter) {
			return {
				filterType,
				operator: 'AND',
				condition1: referenceFilter,
				condition2: itemFilter,
			};
		} else if (referenceFilter) {
			return referenceFilter;
		} else if (itemFilter) {
			return itemFilter;
		}
		return undefined;
	}

	private getAgTextFilter(values: FilterValues | string): AgTextFilter | undefined {
		if (typeof values !== 'string' || !values.trim()) {
			return undefined;
		}
		return {
			filterType: 'text',
			type: 'contains',
			filter: values.trim()
		};
	}

	private getSteelUserTextFilter(currentFilter: unknown, values: FilterValues | string): TextFilter | undefined {
		if (currentFilter !== undefined && typeof currentFilter !== 'object') {
			throw new TypeError('Current filter is not an object');
		}
		const textFilter = <TextFilter | undefined> currentFilter;
		if (typeof values !== 'string' || !values) {
			return undefined;
		}
		const value = values.trim();
		if (!value) {
			return undefined;
		}
		if (textFilter) {
			if (!textFilter.searchTexts) {
				textFilter.searchTexts = [];
			}
			textFilter.searchTexts.push(value);
			return textFilter;
		}
		return {
			customFilterName: FilterTypeValue.TEXT_FILTER,
			searchTexts: [value]
		};
	}

	private getSteelUserNumberFilter(
		currentFilter: unknown,
		values: FilterValues | string
	): NumberFilter | undefined {
		if (currentFilter !== undefined && typeof currentFilter === 'string') {
			throw new TypeError('Current filter is not an object');
		}
		const numberFilter = <NumberFilter | undefined> currentFilter;
		if (typeof values === 'string' || typeof values.from !== 'string' || typeof values.to !== 'string') {
			return undefined;
		}
		const range: NumberRange = {
			lessThan: this.getNumber(values.to),
			moreThan: this.getNumber(values.from)
		};
		if (!range.lessThan && !range.moreThan) {
			return undefined;
		}
		if (numberFilter) {
			if (!numberFilter.ranges) {
				numberFilter.ranges = [];
			}
			numberFilter.ranges.push(range);
			return numberFilter;
		}
		return {
			customFilterName: FilterTypeValue.NUMBER_FILTER,
			ranges: [range]
		};
	}

	private getSteelUserDateRangeFilter(
		currentFilter: unknown,
		values: FilterValues | string
	): DateRangeFilter | undefined {
		if (currentFilter !== undefined && typeof currentFilter === 'string') {
			throw new TypeError('Current filter is not an object');
		}
		const dateFilter = <DateRangeFilter | undefined> currentFilter;
		if (typeof values === 'string') {
			return undefined;
		}
		const range: DateRange = {
			from: this.getDate(values.from),
			to: this.getDate(values.to)
		};
		if (!range.from && !range.to) {
			return undefined;
		}
		if (dateFilter) {
			if (!dateFilter.ranges) {
				dateFilter.ranges = [];
			}
			dateFilter.ranges.push(range);
			return dateFilter;
		}
		return {
			customFilterName: FilterTypeValue.DATE_RANGE_FILTER,
			ranges: [range]
		};
	}

	private getSteelUserOrderNumberFilter(
		currentFilter: unknown,
		values: FilterValues | string
	): OrderNumberFilter | undefined {
		if (currentFilter !== undefined && typeof currentFilter === 'string') {
			throw new TypeError('Current filter is not an object');
		}
		const orderNumberFilter = <OrderNumberFilter | undefined> currentFilter;
		if (typeof values === 'string') {
			return undefined;
		}
		const orderNumber: OrderNumber = {
			reference: values.reference,
			item: values.item
		};
		if (!orderNumber.reference && !orderNumber.item) {
			return undefined;
		}
		if (orderNumberFilter) {
			if (!orderNumberFilter.list) {
				orderNumberFilter.list = [];
			}
			orderNumberFilter.list.push(orderNumber);
			return orderNumberFilter;
		}
		return {
			customFilterName: FilterTypeValue.ORDER_NUMBER_FILTER,
			list: [orderNumber]
		};
	}

	private getNumber(value?: string) {
		if (!isNumber(value)) {
			return undefined;
		}
		const number = Number(value);
		if (Number.isNaN(number)) {
			return undefined;
		}
		return number;
	}

	private getDate(value?: string | Date) {
		if (!value) {
			return undefined;
		}
		if (value instanceof Date) {
			return value;
		}
		if (isNumber(value)) {
			return undefined;
		}
		const date = new Date(value);
		if (Number.isNaN(date)) {
			return undefined;
		}
		return date;
	}

	private createLuceneNode(currentNode: LuceneExpression | undefined, newNode: LuceneExpression | undefined) {
		if (!newNode) {
			return currentNode;
		}
		return !currentNode ? newNode
			: {
				left: currentNode,
				operator: 'AND',
				parenthesized: true,
				right: newNode
			};
	}
}
